import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { IngredientItem, ProductItem, ConversionItem, BomItem, InwardDropdownOption } from '../types';
import { getUniqueUnits } from '../constants/units';
import { formatQty } from '../utils/format';
import { toComparisonKey } from '../utils/textNormalizer';
import toast from 'react-hot-toast';

interface CachedMasterData {
  ingredients: IngredientItem[];
  products: ProductItem[];
  boms: BomItem[];
  conversions: ConversionItem[];
  timestamp: number;
}

let globalCache: CachedMasterData | null = null;

export function useMasterData() {
  const [ingredients, setIngredients] = useState<IngredientItem[]>(() => globalCache?.ingredients || []);
  const [products, setProducts] = useState<ProductItem[]>(() => globalCache?.products || []);
  const [boms, setBoms] = useState<BomItem[]>(() => globalCache?.boms || []);
  const [conversions, setConversions] = useState<ConversionItem[]>(() => globalCache?.conversions || []);
  const [loading, setLoading] = useState<boolean>(() => !globalCache);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (force = false) => {
    // Cache valid for 5 minutes unless forced
    if (!force && globalCache && Date.now() - globalCache.timestamp < 300000) {
      setIngredients(globalCache.ingredients);
      setProducts(globalCache.products);
      setBoms(globalCache.boms);
      setConversions(globalCache.conversions);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await api.getMasterData();
      const ingList = data.ingredients || [];
      const prodList = data.products || [];
      const bomList = data.boms || [];
      const rawConvList = data.conversions || [];

      // Auto-heal map: ingredient name -> standard unit
      const ingUnitMap = new Map<string, string>();
      for (const ing of ingList) {
        ingUnitMap.set(ing.name.trim().toLowerCase(), ing.unit || 'g');
      }

      // Filter out any #ERROR! or # in standard_unit across all conversions
      const convList = rawConvList.map((c) => {
        let std = (c.standard_unit || '').trim();
        if (!std || std.includes('#') || std.toUpperCase().includes('ERROR')) {
          std = ingUnitMap.get(c.ingredient_name.trim().toLowerCase()) || 'g';
        }
        return { ...c, standard_unit: std };
      });

      globalCache = {
        ingredients: ingList,
        products: prodList,
        boms: bomList,
        conversions: convList,
        timestamp: Date.now(),
      };

      setIngredients(ingList);
      setProducts(prodList);
      setBoms(bomList);
      setConversions(convList);
    } catch (err: any) {
      console.error('Failed to load master data:', err);
      const errMsg = err?.message || 'Không thể kết nối đến máy chủ';
      setError(errMsg);
      toast.error(`Không thể tải dữ liệu danh mục: ${errMsg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derived lists
  const ingredientNames = Array.from(new Set(ingredients.map((i) => i.name).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, 'vi')
  );

  const productNames = Array.from(new Set(products.map((p) => p.name).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, 'vi')
  );

  const ingredientCategories = Array.from(
    new Set(ingredients.map((i) => i.category || 'Khác').filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'vi'));

  const productCategories = Array.from(
    new Set(products.map((p) => p.category || 'Đồ uống').filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'vi'));

  const units = getUniqueUnits(ingredients.map((i) => i.unit));

  // Raw ingredients only (strictly exclude PREPARED / BTP)
  const rawIngredients = useMemo(
    () => ingredients.filter((i) => i.type !== 'PREPARED'),
    [ingredients]
  );

  /**
   * Inward items list for Luồng 1 (Nhập Hàng Kho Tổng):
   * Kho Tổng NEVER receives BTP (Bán thành phẩm).
   * Kho Tổng always receives RAW materials, primarily defined in Google Sheet: 04_Quy_doi.
   */
  const inwardItems = useMemo<InwardDropdownOption[]>(() => {
    const ingredientMap = new Map<string, IngredientItem>();
    for (const ing of ingredients) {
      ingredientMap.set(ing.name.trim().toLowerCase(), ing);
    }

    const seen = new Set<string>();
    const list: InwardDropdownOption[] = [];

    // Items strictly from 04_Quy_doi (Conversion list - primary source of truth)
    // Absolutely NO silent fallback to 02_Nguyen_lieu!
    for (const c of conversions) {
      const rawName = c.ingredient_name.trim();
      const pkgUnit = c.package_unit.trim();
      if (!rawName || !pkgUnit) continue;

      const key = `${rawName.toLowerCase()}:::${pkgUnit.toLowerCase()}`;
      if (seen.has(key)) continue;

      const ingMeta = ingredientMap.get(rawName.toLowerCase());
      // Strictly exclude BTP / PREPARED
      if (ingMeta && ingMeta.type === 'PREPARED') continue;

      seen.add(key);

      let cleanStdUnit = c.standard_unit ? c.standard_unit.trim() : '';
      if (!cleanStdUnit || cleanStdUnit.includes('#') || cleanStdUnit.toUpperCase().includes('ERROR')) {
        cleanStdUnit = ingMeta?.unit || 'g';
      }

      const ratioText =
        c.ratio > 1
          ? `${rawName} - ${pkgUnit} (1 ${pkgUnit} = ${formatQty(c.ratio, cleanStdUnit)})`
          : `${rawName} (${pkgUnit})`;

      list.push({
        key,
        name: rawName,
        category: ingMeta?.category || 'Nguyên liệu nhập',
        unit: cleanStdUnit,
        package_unit: pkgUnit,
        ratio: c.ratio || 1,
        displayName: ratioText,
        type: 'RAW',
      });
    }

    // Sort by Category then Name for clean UI grouping
    return list.sort((a, b) => {
      const catCmp = a.category.localeCompare(b.category, 'vi');
      if (catCmp !== 0) return catCmp;
      return a.name.localeCompare(b.name, 'vi');
    });
  }, [conversions, ingredients]);

  /**
   * Transfer items list for Luồng 2 (Chuyển Kho Tổng -> Quầy Bar):
   * Kho Tổng chỉ xuất Nguyên liệu thô (RAW), hỗ trợ 1 nguyên liệu có nhiều hình thức nhập
   * (ví dụ cả Thùng và Gói từ tab 04_Quy_doi). Đồng thời bổ sung các nguyên liệu thô chưa có trong 04_Quy_doi.
   */
  const transferItems = useMemo<InwardDropdownOption[]>(() => {
    const list = [...inwardItems];
    const seenNames = new Set<string>();
    for (const item of inwardItems) {
      seenNames.add(toComparisonKey(item.name));
    }

    // Bổ sung các nguyên liệu thô chưa khai báo trong 04_Quy_doi
    for (const ing of rawIngredients) {
      const key = toComparisonKey(ing.name);
      if (!seenNames.has(key)) {
        list.push({
          key: ing.name.toLowerCase(),
          name: ing.name,
          category: ing.category || 'Nguyên liệu khác',
          unit: ing.unit || 'g',
          package_unit: ing.unit || 'g',
          ratio: 1,
          displayName: `${ing.name} (${ing.unit || 'g'})`,
          type: 'RAW',
        });
      }
    }

    return list.sort((a, b) => {
      const catCmp = a.category.localeCompare(b.category, 'vi');
      if (catCmp !== 0) return catCmp;
      return a.name.localeCompare(b.name, 'vi');
    });
  }, [inwardItems, rawIngredients]);

  /**
   * Get specific packaging units configured for an item in 04_Quy_doi,
   * followed by standard unit and general packaging units.
   */
  const getInwardUnitsForItem = useCallback(
    (ingredientName: string): string[] => {
      if (!ingredientName) return units;
      const cleanName = ingredientName.trim().toLowerCase();
      const cleanKey = toComparisonKey(ingredientName);

      // Find matched conversion package units from 04_Quy_doi
      const matched = conversions.filter(
        (c) =>
          toComparisonKey(c.ingredient_name) === cleanKey ||
          c.ingredient_name.trim().toLowerCase() === cleanName
      );
      const specificUnits = matched.map((c) => c.package_unit.trim()).filter(Boolean);

      const ingMeta = ingredients.find(
        (i) => toComparisonKey(i.name) === cleanKey || i.name.trim().toLowerCase() === cleanName
      );
      const stdUnit = matched[0]?.standard_unit || ingMeta?.unit;

      const ordered = new Set<string>();
      // 1. Configured units from 04_Quy_doi first
      specificUnits.forEach((u) => ordered.add(u));
      // 2. Standard unit
      if (stdUnit) ordered.add(stdUnit);
      // 3. Common packaging and metric units
      ['Thùng', 'Hộp', 'Lon', 'Bao', 'Bịch', 'Chai', 'Gói', 'Túi', 'kg', 'g', 'l', 'ml'].forEach((u) =>
        ordered.add(u)
      );

      return Array.from(ordered);
    },
    [conversions, ingredients, units]
  );

  /**
   * Convert an item's inward packaging quantity into standard unit
   * using Master Data 04_Quy_doi and built-in metric rules.
   */
  const convertInwardItem = useCallback(
    (
      ingredientName: string,
      packageUnit: string,
      quantity: number
    ): {
      converted: boolean;
      standard_quantity: number;
      standard_unit: string;
      ratio: number;
      explanation?: string;
      error?: string;
    } => {
      const cleanName = (ingredientName || '').trim().toLowerCase();
      const cleanUnit = (packageUnit || '').trim().toLowerCase();
      const cleanIngKey = toComparisonKey(ingredientName);
      const cleanPkgKey = toComparisonKey(packageUnit);
      const qty = Math.max(0, quantity);

      if (!cleanIngKey) {
        return {
          converted: false,
          standard_quantity: 0,
          standard_unit: packageUnit || 'đơn vị',
          ratio: 0,
          error: 'Chưa chọn tên nguyên liệu',
        };
      }

      // 1. Canonical match in 04_Quy_doi (ingredient + package unit)
      const rule = conversions.find((c) => {
        const cIngKey = toComparisonKey(c.ingredient_name);
        const cUnitKey = toComparisonKey(c.package_unit);
        return (
          (cIngKey === cleanIngKey && cUnitKey === cleanPkgKey) ||
          (c.ingredient_name.trim().toLowerCase() === cleanName &&
            c.package_unit.trim().toLowerCase() === cleanUnit)
        );
      });

      if (rule && rule.ratio > 0) {
        const stdQty = Number((qty * rule.ratio).toFixed(2));
        let stdUnit = (rule.standard_unit || '').trim();
        if (!stdUnit || stdUnit.includes('#') || stdUnit.toUpperCase().includes('ERROR')) {
          const ingMeta = ingredients.find(
            (i) => toComparisonKey(i.name) === cleanIngKey || i.name.trim().toLowerCase() === cleanName
          );
          stdUnit = ingMeta?.unit || 'g';
        }
        return {
          converted: true,
          standard_quantity: stdQty,
          standard_unit: stdUnit,
          ratio: rule.ratio,
          explanation: `1 ${packageUnit.trim()} = ${formatQty(rule.ratio, stdUnit)}`,
        };
      }

      // 2. Metric rules (kg -> g, l/lit -> ml)
      const metricRules: Record<string, { unit: string; ratio: number }> = {
        kg: { unit: 'g', ratio: 1000 },
        kilogram: { unit: 'g', ratio: 1000 },
        kilo: { unit: 'g', ratio: 1000 },
        l: { unit: 'ml', ratio: 1000 },
        lit: { unit: 'ml', ratio: 1000 },
        lít: { unit: 'ml', ratio: 1000 },
        liter: { unit: 'ml', ratio: 1000 },
      };
      const m = metricRules[cleanUnit];
      if (m) {
        const stdQty = Number((qty * m.ratio).toFixed(2));
        return {
          converted: true,
          standard_quantity: stdQty,
          standard_unit: m.unit,
          ratio: m.ratio,
          explanation: `1 ${packageUnit.trim()} = ${formatQty(m.ratio, m.unit)}`,
        };
      }

      // 3. NO SILENT FALLBACK: Missing in 04_Quy_doi -> return converted: false with explicit error
      return {
        converted: false,
        standard_quantity: 0,
        standard_unit: packageUnit.trim() || 'đơn vị',
        ratio: 0,
        error: `Chưa có quy cách quy đổi trong bảng 04_Quy_doi cho "${ingredientName}" (${packageUnit}). Vui lòng nhập vào Google Sheet rồi sync về!`,
      };
    },
    [conversions]
  );

  const getIngredient = (name: string): IngredientItem | undefined => {
    if (!name) return undefined;
    const lower = name.trim().toLowerCase();
    return ingredients.find((i) => i.name.toLowerCase() === lower);
  };

  const getProduct = (name: string): ProductItem | undefined => {
    if (!name) return undefined;
    const lower = name.trim().toLowerCase();
    return products.find((p) => p.name.toLowerCase() === lower);
  };

  return {
    ingredients,
    rawIngredients,
    boms,
    conversions,
    inwardItems,
    transferItems,
    products,
    ingredientNames,
    productNames,
    ingredientCategories,
    productCategories,
    units,
    getInwardUnitsForItem,
    convertInwardItem,
    getIngredient,
    getProduct,
    loading,
    error,
    refresh: () => loadData(true),
  };
}
